import type { WeChatProfile } from '../shared/types';
import { wxCall } from './wx-call';
export interface Hit { id: string; label: string; x: number; y: number; w: number; h: number; disabled?: boolean; action: () => void; drag?: boolean; onAdjust?: (direction: -1 | 1) => void; onScroll?: (delta:number)=>void; wechatProfile?: (profile: WeChatProfile | null, error?: Error) => void }
export interface Pointer {
  x: number; y: number; id: number;
  /** Complete active native contacts after this event; absent for web pointer events. */
  touches?: { x: number; y: number; id: number }[];
}
export interface PlatformSocketHandlers {
  onOpen(): void;
  onMessage(data: string): void;
  onClose(): void;
  onError(error: Error): void;
}
export interface PlatformSocket { send(data: string): Promise<void>; close(): void }
export interface PlatformKeyboardOptions {
  value: string; maxLength: number;
  onInput(value: string): void;
  onConfirm(value: string): void;
  onClose(): void;
  onHeight(height: number): void;
}
export interface Platform {
  durable?: import('./durable-storage').DurableStorage;

  sound?(kind: import('./flight-audio').FlightSound): void;
  music?(state: import('./flight-audio').FlightMusicState | null): void;
  audioSettings?(settings: { sound: boolean; music: boolean }): void;
  unlockAudio?(): void;
  silenceAudio?(): void;
  canvas: HTMLCanvasElement;
  width: number; height: number; dpr: number; topInset: number;
  safeInsets?: import('./layout').Insets;
  menuRect?: import('./layout').Rect;
  onResize?(handler: () => void): void;
  request<T>(path: string, method: string, data?: unknown, token?: string, key?: string): Promise<T>;
  connectSocket?(path: string, handlers: PlatformSocketHandlers): PlatformSocket;
  openKeyboard?(options: PlatformKeyboardOptions): Promise<void>;
  closeKeyboard?(): void;
  credentials(): Promise<{ provider: 'guest'; secret: string } | { provider: 'wechat'; code: string }>;
  supportsWeChatProfile?: boolean;
  authorizedProfile?: () => Promise<WeChatProfile | null>;
  openPermissions?: () => Promise<void>;
  openPrivacy?: () => Promise<void>;
  prepareWeChatAvatar?: (url: string) => Promise<string>;
  loadImage?: (url: string) => Promise<HTMLImageElement>;
  chooseAvatar?: () => Promise<string | null>;
  get<T>(key: string, fallback: T): T;
  set(key: string, value: unknown): void;
  remove(key: string): void;
  clearAccount(): void;
  prompt(title: string, placeholder: string, initial?: string): Promise<string | null>;
  confirm(title: string, message: string): Promise<boolean>;
  choose(title: string, options: string[]): Promise<number | null>;
  consent(): Promise<boolean>;
  share(code: string, options?: { title?: string; mode?: import('../shared/modes').ModeId; room?: boolean }): Promise<void>;
  requestSubscribe?: () => Promise<boolean>;
  copyText?: (text: string) => Promise<void>;
  export(drawingCanvas: HTMLCanvasElement): Promise<void>;
  vibrate(strength?: 'light' | 'medium' | 'heavy'): void;
  createCanvas(width: number, height: number): HTMLCanvasElement;
  /** Hit rectangles are window coordinates, without DPR; adapters must not scale again. */
  controls(hits: Hit[], title: string): void;
  onPointer(handler: (kind: 'down' | 'move' | 'up' | 'cancel', pointer: Pointer) => void): void;
  onResume(handler: (invite?: string) => void): void;
  onPause(handler: () => void): void;
  initialInvite?: string;
  initialRoom?: string;
  onRoomInvite?(handler: (code: string) => void): void;
}
export class ApiError extends Error {
  constructor(public status: number, message: string, public retryAfterMs?: number) { super(message); }
}
/** HTTP Retry-After accepts seconds or an HTTP date. Header names are case insensitive. */
export function retryAfterMs(headers: Record<string, unknown> | undefined, now = Date.now()): number | undefined {
  const value = Object.entries(headers ?? {}).find(([key]) => key.toLowerCase() === 'retry-after')?.[1];
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const text = String(value).trim(); if (!text) return undefined;
  const delay = /^\d+(?:\.\d+)?$/.test(text) ? Number(text) * 1000 : Date.parse(text) - now;
  return Number.isFinite(delay) && delay >= 0 ? delay : undefined;
}
export const requestId = () => `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;

export function socketUrl(apiBase: string, path: string): string {
  if (!/^https?:\/\//.test(apiBase) || !/^\/api(?:\/|$)/.test(path) || /[?#]/.test(path)) throw new Error('实时连接地址无效。');
  return apiBase.replace(/^http/, 'ws').replace(/\/$/, '') + path;
}

export function connectWebSocket(apiBase: string, path: string, handlers: PlatformSocketHandlers, Socket = WebSocket): PlatformSocket {
  const task = new Socket(socketUrl(apiBase, path));
  let closed = false;
  const finish = () => {
    if (closed) return;
    closed = true; clearTimeout(timer);
    task.onopen = task.onmessage = task.onerror = task.onclose = null;
    handlers.onClose();
  };
  const timer = setTimeout(() => {
    if (closed || task.readyState === 1) return;
    handlers.onError(new Error('实时连接超时，请重试。')); task.close(); finish();
  }, 12_000);
  task.onopen = () => { clearTimeout(timer); if (!closed) handlers.onOpen(); };
  task.onmessage = event => { if (!closed && typeof event.data === 'string') handlers.onMessage(event.data); };
  task.onerror = () => { if (!closed) handlers.onError(new Error('实时连接暂时断开。')); };
  task.onclose = finish;
  return {
    async send(data) {
      if (closed || task.readyState !== 1) throw new Error('实时连接尚未就绪。');
      if (task.bufferedAmount > 1_000_000) throw new Error('网络较慢，正在同步笔迹。');
      task.send(data);
    },
    close() { if (!closed) { task.close(); finish(); } },
  };
}

export function connectWeChatSocket(wx: any, apiBase: string, path: string, handlers: PlatformSocketHandlers): PlatformSocket {
  let task: any; let opened = false; let closed = false;
  let rejectOpening: (reason: Error) => void = () => {};
  let open: () => void; let message: (event: { data: unknown }) => void;
  let error: (reason: any) => void; let close: () => void;
  const finish = () => {
    if (closed) return;
    closed = true; rejectOpening(new Error('实时连接已关闭。'));
    task?.offOpen?.(open); task?.offMessage?.(message); task?.offError?.(error); task?.offClose?.(close);
    handlers.onClose();
  };
  const shutdown = () => {
    if (closed) return;
    finish();
    if (task) void wxCall<void>(({ success, fail }) => task.close({ success: () => success(undefined), fail }), { timeout: 3000, timeoutValue: undefined }).catch(() => {});
  };
  const connection = wxCall<void>(({ success, fail }) => {
    rejectOpening = fail;
    open = () => { if (!closed) { opened = true; success(undefined); handlers.onOpen(); } };
    message = event => { if (!closed && typeof event.data === 'string') handlers.onMessage(event.data); };
    error = reason => {
      if (closed) return;
      const failure = new Error(String(reason?.errMsg ?? '实时连接暂时断开。'));
      if (!opened) fail(failure); else { handlers.onError(failure); shutdown(); }
    };
    close = finish;
    task = wx.connectSocket({ url: socketUrl(apiBase, path), success: () => {}, fail });
    task.onOpen(open); task.onMessage(message); task.onError(error); task.onClose(close);
  }, { onTimeout: () => new Error('实时连接超时，请重试。') });
  void connection.catch(reason => { if (!closed) { handlers.onError(reason instanceof Error ? reason : new Error('实时连接失败。')); shutdown(); } });
  return {
    send(data) {
      if (!opened || closed) return Promise.reject(new Error('实时连接尚未就绪。'));
      return wxCall<void>(({ success, fail }) => task.send({ data, success: () => success(undefined), fail: (reason: any) => fail(new Error(String(reason?.errMsg ?? '笔迹发送失败。'))) }), { onTimeout: () => new Error('笔迹发送超时，请重连后重试。') });
    },
    close: shutdown,
  };
}

export function createWeChatKeyboard(wx: any) {
  let dispose: (() => void) | undefined;
  const hide = () => {
    if (typeof wx.hideKeyboard === 'function') void wxCall<void>(({ success, fail }) => wx.hideKeyboard({ success: () => success(undefined), fail }), { timeout: 3000, timeoutValue: undefined }).catch(() => {});
  };
  return {
    async open(options: PlatformKeyboardOptions) {
      dispose?.();
      if (typeof wx.showKeyboard !== 'function') throw new Error('当前微信版本不支持实时输入，请更新微信。');
      let active = true;
      const finish = () => {
        if (!active) return;
        active = false;
        wx.offKeyboardInput?.(input); wx.offKeyboardConfirm?.(confirm); wx.offKeyboardComplete?.(complete); wx.offKeyboardHeightChange?.(height);
        if (dispose === finish) dispose = undefined;
        options.onHeight(0); options.onClose();
      };
      const input = (event: any) => { if (active) options.onInput(String(event.value ?? '')); };
      const confirm = (event: any) => { if (active) { options.onConfirm(String(event.value ?? '')); finish(); } };
      const complete = (event: any) => { if (active) { options.onInput(String(event.value ?? '')); finish(); } };
      const height = (event: any) => { if (active) options.onHeight(Math.max(0, Number(event.height) || 0)); };
      dispose = finish;
      wx.onKeyboardInput(input); wx.onKeyboardConfirm(confirm); wx.onKeyboardComplete(complete); wx.onKeyboardHeightChange?.(height);
      try {
        await wxCall<void>(({ success, fail }) => wx.showKeyboard({ defaultValue: options.value, maxLength: options.maxLength, multiple: false, confirmHold: false, confirmType: 'send', success: () => success(undefined), fail: () => fail(new Error('键盘暂时无法打开，请重试。')) }), { onTimeout: () => new Error('打开键盘超时，请重试。') });
      } catch (error) { if (active) { finish(); hide(); } throw error; }
    },
    close() { if (dispose) { dispose(); hide(); } },
  };
}

export function createWebKeyboard(anchor: HTMLElement) {
  let dispose: (() => void) | undefined;
  return {
    async open(options: PlatformKeyboardOptions) {
      dispose?.();
      const bar = document.createElement('form'); const field = document.createElement('input'); const cancel = document.createElement('button');
      field.type = 'text'; field.value = options.value; field.maxLength = options.maxLength; field.autocomplete = 'off';
      field.setAttribute('aria-label', '猜词答案'); field.enterKeyHint = 'send';
      cancel.type = 'button'; cancel.textContent = '收起';
      Object.assign(bar.style, { position: 'fixed', zIndex: '100', boxSizing: 'border-box', display: 'flex', gap: '8px', padding: '8px', background: '#FFFFFF', borderTop: '1px solid #D5DFE5', margin: '0' });
      Object.assign(field.style, { flex: '1', minWidth: '0', height: '38px', boxSizing: 'border-box', font: '16px sans-serif', border: '1px solid #93B4CB', borderRadius: '6px', padding: '0 10px', color: '#263D46', background: '#FFFFFF' });
      Object.assign(cancel.style, { flex: '0 0 52px', border: '0', background: '#FFFFFF', color: '#2274B8', font: '14px sans-serif' });
      bar.append(field, cancel); document.body.append(bar);
      let active = true;
      const update = () => {
        if (!active) return;
        const viewport = window.visualViewport; const keyboardHeight = viewport ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0;
        const rect = anchor.getBoundingClientRect();
        bar.style.left = `${rect.left}px`; bar.style.width = `${Math.min(rect.width, window.innerWidth)}px`; bar.style.bottom = `${keyboardHeight}px`;
        options.onHeight(keyboardHeight + bar.getBoundingClientRect().height);
      };
      const finish = () => {
        if (!active) return;
        active = false; window.removeEventListener('resize', update); window.visualViewport?.removeEventListener('resize', update); window.visualViewport?.removeEventListener('scroll', update);
        bar.remove(); if (dispose === finish) dispose = undefined;
        options.onHeight(0); options.onClose();
      };
      let composing = false;
      dispose = finish; field.oninput = () => options.onInput(field.value);
      field.addEventListener('compositionstart', () => { composing = true; });
      field.addEventListener('compositionend', () => { composing = false; });
      field.onkeydown = event => {
        if (event.key === 'Escape') { event.preventDefault(); finish(); }
        if (event.key === 'Enter' && (composing || event.isComposing)) event.preventDefault();
      };
      bar.onsubmit = event => { event.preventDefault(); options.onConfirm(field.value); finish(); };
      cancel.onclick = finish;
      window.addEventListener('resize', update); window.visualViewport?.addEventListener('resize', update); window.visualViewport?.addEventListener('scroll', update);
      update(); field.focus();
    },
    close() { dispose?.(); },
  };
}

